import type { Ref } from "lit/directives/ref.js";
import { computePosition, flip, offset, shift, type Placement } from "@floating-ui/dom";
import inputVariables from "@krystal/css/components/input.css?inline";
import { html, nothing, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import SlotController from "@/common/controllers/slot";
import { when } from "@/common/directives/when";
import "@/components/input";
import "@/components/badge";
import { cn } from "@/utilities/css";
import EkoElement from "@/utilities/element";
import type Option from "./option";
import style from "./select-advanced.css?inline";

@customElement("eko-select-advanced")
class SelectAdvanced extends EkoElement {
  static formAssociated = true;

  static styles = [...EkoElement.styles, unsafeCSS(inputVariables), unsafeCSS(style)];

  private _initialSetupComplete: boolean = false;

  private _selectedOption: Option[] = [];
  private _selectedOptions: Option[] = [];

  private afterSlot = new SlotController(this, "after");
  private beforeSlot = new SlotController(this, "before");

  @state() private focusedOptionIndex: number = 0;

  private handleBlur = (event: FocusEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement;

    if (relatedTarget && this.contains(relatedTarget)) {
      return;
    }

    if (this.isOpening) {
      return;
    }

    if (this.keyword.length > 0) {
      window.queueMicrotask(() => {
        const activeElement = document.activeElement as HTMLElement;

        if (activeElement && this.contains(activeElement)) {
          return;
        }

        if (activeElement?.tagName === "EKO-OPTION") {
          return;
        }

        this.hideSelect();
      });
      return;
    }

    this.hideSelect();
  };

  private handleDocumentClick = (event: Event) => {
    const target = event.target as HTMLElement;

    if (!this.contains(target)) {
      this.hideSelect();
    }
  };

  private handleDocumentFocusIn = (event: FocusEvent) => {
    const target = event.target as HTMLElement;

    if (target.tagName === "EKO-OPTION" && this.contains(target)) {
      this.isOpening = false;
      return;
    }
  };

  private handleInputChange = (e: Event) => {
    const input = e.target as HTMLInputElement;
    this.keyword = input.value;
    this.showSelect();
    this.setPosition();
  };

  private handleKeyDown = (event: Event) => {
    const keyboardEvent = event as globalThis.KeyboardEvent;
    let options = [];
    if (this.keyword.length > 0) {
      options = this.filteredOptions;
    } else {
      options = Array.from(this.querySelectorAll("eko-option")) as Option[];
    }

    if (options.length === 0) return;

    // If dropdown is not open, open it on ArrowDown/ArrowUp/Enter/Space
    const isOpen = this.selectRef.value?.classList.contains("eko-select__content-active");

    // Handle characters for typeahead
    if (
      !this.multiple &&
      keyboardEvent.key.length === 1 &&
      !keyboardEvent.ctrlKey &&
      !keyboardEvent.metaKey &&
      !keyboardEvent.altKey &&
      keyboardEvent.key !== " "
    ) {
      keyboardEvent.preventDefault();

      if (this.handleTypeahead(keyboardEvent.key, options)) {
        if (!isOpen) {
          this.showSelect(true, true);
        } else {
          this.updateFocusedOption();
        }
      }
      return;
    }

    if (!isOpen) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(keyboardEvent.key)) {
        keyboardEvent.preventDefault();

        // Determine initial focus position based on key and selection
        let initialIndex: number | undefined;
        const selectedIndex = options.findIndex((opt) => this.selected.includes(opt.value));

        if (keyboardEvent.key === "ArrowUp") {
          // ArrowUp opens to last option, or selected if exists
          initialIndex = selectedIndex >= 0 ? selectedIndex : options.length - 1;
        } else {
          // ArrowDown/Enter/Space opens to first option, or selected if exists
          initialIndex = selectedIndex >= 0 ? selectedIndex : 0;
        }

        this.showSelect(false, true, initialIndex);
      }
      return;
    }

    // Sync focusedOptionIndex with currently focused option in case user tabbed to an option
    const activeElement = document.activeElement;
    if (activeElement && activeElement.tagName === "EKO-OPTION") {
      const activeIndex = options.indexOf(activeElement as Option);
      if (activeIndex >= 0) {
        this.focusedOptionIndex = activeIndex;
      }
    }

    const currentIndex = this.focusedOptionIndex;
    let targetIndex = -1;

    switch (keyboardEvent.key) {
      case "ArrowDown":
        keyboardEvent.preventDefault();
        targetIndex = currentIndex === options.length - 1 ? 0 : currentIndex + 1;
        break;
      case "ArrowUp":
        keyboardEvent.preventDefault();
        targetIndex = currentIndex === 0 ? options.length - 1 : currentIndex - 1;
        break;
      case "Enter":
      case " ":
        if (currentIndex !== undefined) {
          const targetOption = options[currentIndex];

          if (targetOption.href) {
            // This isn't ideal but focus is on the option so we need
            // to simulate a click on the link inside the menu item
            targetOption.shadowRoot
              ?.querySelector("eko-menu-item")
              ?.shadowRoot?.querySelector("a")
              ?.click();
          } else if (currentIndex >= 0) {
            keyboardEvent.preventDefault();
            this.selectOption(targetOption);
          }
        }
        return;
      case "Escape":
        keyboardEvent.preventDefault();
        this.hideSelect();
        this.triggerRef.value?.querySelector("input")?.focus();
        return;
      case "Tab":
        if (this.multiple && activeElement?.tagName !== "EKO-OPTION" && !keyboardEvent.shiftKey) {
          keyboardEvent.preventDefault();

          this.updateComplete.then(() => {
            if (options.length > 0) {
              this.focusedOptionIndex = 0;
              this.updateFocusedOption(true);
            }
          });
        }
        return;
      default:
        return;
    }

    if (targetIndex >= 0) {
      this.focusedOptionIndex = targetIndex;
      this.updateFocusedOption();
    }
  };

  private handleRemoveSelected = (event: Event) => {
    if (this.disabled) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const target = event.target as HTMLElement;
    const menuItem = target.closest("eko-option") as Option;

    if (!menuItem) return;

    this.selected = this.selected.filter((item) => item !== menuItem.value);
  };

  private handleSelect = (event: Event) => {
    const target = event.target as HTMLElement;
    const menuItem = target.closest("eko-option") as Option;

    if (menuItem && !menuItem.href) {
      event.preventDefault();
      event.stopPropagation();
      this.selectOption(menuItem);
    }
  };

  private handleTypeahead = (char: string, options: Option[]) => {
    // Clear timeout if exists
    if (this.typeaheadTimeout !== null) {
      window.clearTimeout(this.typeaheadTimeout);
    }

    // Check if typing the same character repeatedly
    const isSameChar = char === this.typeaheadString;

    // Add character to search string (unless it's a repeat of the same char and we're cycling)
    if (!isSameChar || this.typeaheadString.length === 0) {
      this.typeaheadString += char;
      this.lastMatchIndex = -1;
    }

    // Set new timeout to clear the search string after 1 second
    this.typeaheadTimeout = window.setTimeout(() => {
      this.typeaheadString = "";
      this.lastMatchIndex = -1;
      this.typeaheadTimeout = null;
    }, 1000);

    // Find matching options
    const searchString = this.typeaheadString.toLowerCase();
    let matchIndex = -1;

    if (isSameChar && this.typeaheadString.length === 1) {
      // Cycling through options starting with the same character
      const startIndex = this.lastMatchIndex >= 0 ? this.lastMatchIndex + 1 : 0;

      // Search from current position to end
      for (let i = startIndex; i < options.length; i++) {
        const optionText = options[i].textContent?.trim().toLowerCase() || "";
        if (optionText.startsWith(searchString)) {
          matchIndex = i;
          break;
        }
      }

      // If not found, wrap around and search from beginning
      if (matchIndex === -1) {
        for (let i = 0; i < startIndex; i++) {
          const optionText = options[i].textContent?.trim().toLowerCase() || "";
          if (optionText.startsWith(searchString)) {
            matchIndex = i;
            break;
          }
        }
      }
    } else {
      // Find first option matching the full search string
      matchIndex = options.findIndex((option) => {
        const optionText = option.textContent?.trim().toLowerCase() || "";
        return optionText.startsWith(searchString);
      });
    }

    if (matchIndex >= 0) {
      this.lastMatchIndex = matchIndex;
      this.focusedOptionIndex = matchIndex;
      return true;
    }

    return false;
  };

  private internals: ElementInternals;

  private isOpening: boolean = false;

  @state() private keyword: string = "";

  private lastMatchIndex: number = -1;

  private selectRef: Ref<HTMLElement> = createRef();

  private setPosition = () => {
    const triggerElement = this.triggerRef.value?.shadowRoot?.querySelector(
      ".eko-input__field-wrapper"
    );
    if (!triggerElement) return;
    const selectElement = this.selectRef.value!;

    computePosition(triggerElement, selectElement, {
      middleware: [offset(3), flip(), shift({ padding: 5 })],
      placement: this.position as Placement,
    }).then(({ x, y }) => {
      Object.assign(selectElement.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  };

  @state() private slotContents: Option[] = [];

  private triggerRef: Ref<HTMLElement> = createRef();

  private typeaheadString: string = "";
  private typeaheadTimeout: number | null = null;
  @property({ reflect: true, type: Boolean }) admin: boolean = false;
  @property({ reflect: true, type: Boolean }) arrow = false;
  @property({ reflect: true }) class: string = "";
  @property({ reflect: true }) description: string = "";
  @property({ reflect: true, type: Boolean }) disabled: boolean = false;
  @property({ reflect: true }) error: string = "";
  @property({ reflect: true }) help: string = "";
  @property({ reflect: true }) label: string = "";
  @property({ reflect: true, type: Boolean }) multiple: boolean = false;
  @property({ reflect: true }) name: string = "";
  @state() options: NodeListOf<Option> | null = null;
  @property({ reflect: true }) placeholder: string = "";
  @property({ reflect: true }) position: Placement = "bottom";
  @property({ reflect: true, type: Boolean }) required: boolean = false;
  @property() selected: string[] = [];

  @property({ reflect: true, type: Boolean }) showBeforeOnActive: boolean = false;

  constructor() {
    super();
    this.internals = this.attachInternals();
  }

  private handleSlotChange() {
    this.setupClickListeners();
  }

  private hideSelect() {
    this.isOpening = false;
    this.selectRef.value!.classList.remove("eko-select__content-active");
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("focusin", this.handleDocumentFocusIn);
  }

  private selectOption(option: Option) {
    // If an option is selected we update selection and trigger a "change" event.
    const value = option?.value;

    if (!this.selected) {
      this.selected = [];
    }

    if (this.multiple) {
      if (this.selected.includes(value)) {
        this.selected = this.selected.filter((item) => item !== value);
      } else {
        this.selected = [...this.selected, value];
      }
    } else {
      this.selected = [value];
      this.hideSelect();
    }

    this.dispatchEvent(
      new CustomEvent("change", {
        bubbles: true,
        detail: { value: this.selected },
      })
    );
  }

  private setupClickListeners() {
    this.options = this.querySelectorAll("eko-option:not([slot])");
    this.slotContents = Array.from(this.options).map((option) => option.cloneNode(true) as Option);

    this.setSelectedFromDOM(this.options);

    this.options.forEach((item) => {
      item.addEventListener("click", this.handleSelect);
      (item as Option).selected = this.selected.includes((item as Option).value);
      (item as Option).tabIndex = -1;
    });
  }

  private showSelect(
    preserveFocusIndex: boolean = false,
    shouldFocus: boolean = false,
    initialIndex?: number
  ) {
    if (this.disabled) {
      return;
    }

    if (this.selectRef.value?.classList.contains("eko-select__content-active")) {
      return;
    }

    this.isOpening = true;

    this.dispatchEvent(new CustomEvent("open", { bubbles: true }));

    this.selectRef.value!.classList.add("eko-select__content-active");
    this.setPosition();

    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("focusin", this.handleDocumentFocusIn);

    let options = [];
    if (this.keyword.length > 0) {
      options = this.filteredOptions;
    } else {
      options = Array.from(this.querySelectorAll("eko-option")) as Option[];
    }
    if (options.length > 0) {
      // Only set the focused index if we're not preserving it (e.g., from typeahead)
      if (!preserveFocusIndex) {
        if (initialIndex !== undefined) {
          this.focusedOptionIndex = initialIndex;
        } else {
          const selectedIndex = options.findIndex((opt) => this.selected.includes(opt.value));
          this.focusedOptionIndex = selectedIndex >= 0 ? selectedIndex : 0;
        }
      }

      window.requestAnimationFrame(() => {
        // Focus when opening via keyboard navigation, but not when clicking
        this.updateFocusedOption(shouldFocus);
      });
    } else {
      this.isOpening = false;
    }
  }

  private toggleSelect() {
    if (this.disabled) {
      return;
    }

    const isOpen = this.selectRef.value?.classList.contains("eko-select__content-active");

    if (isOpen) {
      this.hideSelect();
    } else {
      this.showSelect();
    }
  }

  private updateFocusedOption(shouldFocus: boolean = true) {
    let options = [];
    if (this.keyword.length > 0) {
      options = this.filteredOptions;
    } else {
      options = Array.from(this.querySelectorAll("eko-option")) as Option[];
    }

    options.forEach((option, index) => {
      const isFocused = index === this.focusedOptionIndex;
      option.tabIndex = isFocused ? 0 : -1;

      if (isFocused && shouldFocus) {
        option.focus();
      }
    });
  }

  private updateFormValue() {
    if (this.selected.length > 0) {
      if (this.multiple) {
        const formData = new globalThis.FormData();
        this.selected.forEach((value) => {
          formData.append(this.name || "select", value);
        });
        this.internals.setFormValue(formData);
      } else {
        this.internals.setFormValue(this.selected[0] || null);
      }
    } else {
      this.internals.setFormValue(null);
    }
  }

  checkValidity(): boolean {
    const input = this.triggerRef.value;
    if (!input) return true;

    // Only validate required if the required property is true
    if (this.required && !this.selected.length) {
      this.internals.setValidity({ valueMissing: true }, "Please select an option", input);
      return false;
    }

    // Clear validation state if valid
    this.internals.setValidity({});
    return true;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.setPosition);
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.setPosition);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("focusin", this.handleDocumentFocusIn);

    if (this.typeaheadTimeout !== null) {
      window.clearTimeout(this.typeaheadTimeout);
      this.typeaheadTimeout = null;
    }

    super.disconnectedCallback();
  }

  firstUpdated() {
    // this.setPosition();
    this.setupClickListeners();
  }

  // Form lifecycle methods
  formAssociatedCallback() {
    // Called when associated with a form
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formResetCallback() {
    this.selected = [];
    this.internals.setFormValue(null);
    this.internals.setValidity({});
  }

  formStateRestoreCallback(state: string | null) {
    if (state) {
      if (this._initialSetupComplete) {
        this.selected = state.split(",");
      }
      this.internals.setFormValue(state);
    } else {
      if (this._initialSetupComplete) {
        this.selected = [];
      }
      this.internals.setFormValue(null);
    }
  }

  render() {
    return html`
      <div class=${cn("eko-select", this.class)} @keydown=${this.handleKeyDown}>
        <eko-input
          label=${this.label}
          @click=${this.toggleSelect}
          @touchstart.passive=${this.toggleSelect}
          @focusout=${this.handleBlur}
          @blur=${this.handleBlur}
          @input=${this.handleInputChange}
          placeholder=${this.placeholder}
          ?disabled=${this.disabled}
          description=${when(this.description)}
          help=${when(this.help)}
          error=${when(this.error)}
          ?readonly=${when(!this.multiple, () => "true")}
          aria-describedby="eko-select"
          ?admin=${this.admin}
          ${ref(this.triggerRef)}>
          ${(this.beforeSlot.isPresent && this.selected.length === 0) ||
          (this.showBeforeOnActive && this.selected.length > 0)
            ? html`<slot name="before" slot="before"></slot>`
            : nothing}
          ${this.selected.length > 0 && this.multiple
            ? html`<div class="eko-select__selected-options" @click=${this.handleRemoveSelected}>
                ${this.selectedOptions}
              </div>`
            : nothing}
          ${this.selected.length > 0 && !this.multiple
            ? html`<div class="eko-select__selected-option">${this.selectedOption}</div>`
            : nothing}
          ${this.afterSlot.isPresent
            ? html`<slot name="after" slot="after"></slot>`
            : html`<eko-icon slot="after" name="chevron-selector-vertical"></eko-icon>`}
        </eko-input>
        <div
          id="eko-select"
          class="eko-select__content"
          role="listbox"
          ${ref(this.selectRef)}
          @click=${this.handleSelect}
          @focusin=${this.showSelect}>
          <div class="eko-select__options">
            ${this.keyword
              ? this.filteredOptions
              : html`<slot
                  class="eko-select__content-text"
                  @slotchange=${this.handleSlotChange}></slot>`}
          </div>
          <slot name="actions" slot="actions"></slot>
        </div>
      </div>
    `;
  }

  reportValidity(): boolean {
    return this.internals.reportValidity();
  }

  setSelectedFromDOM(options: NodeListOf<Option>, defer = false) {
    const selectedFromDOM = Array.from(options)
      .filter((item) => {
        return (item as Option).selected || item.hasAttribute("selected");
      })
      .map((item) => (item as Option).value);

    if (selectedFromDOM.length > 0) {
      // Defer property update until after current update completes to avoid scheduling
      // a new update during an update cycle
      if (defer) {
        this.updateComplete.then(() => {
          this.selected = selectedFromDOM;
        });
      } else {
        this.selected = selectedFromDOM;
      }
    }
  }

  setSelectedFromOptions(options: NodeListOf<Option>) {
    // Update selected state of actual DOM options
    options.forEach((option) => {
      (option as Option).selected = this.selected.includes((option as Option).value);
    });

    // Also update slotContents for consistency
    this.slotContents.forEach((option) => {
      (option as Option).selected = this.selected.includes((option as Option).value);
    });
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("selected")) {
      this.updateFormValue();

      const options = this.querySelectorAll("eko-option");

      if (!this._initialSetupComplete) {
        this.setSelectedFromDOM(options, true);
        this._initialSetupComplete = true;
        return;
      }

      this.setSelectedFromOptions(options);

      this.setPosition();
    }

    if (changedProperties.has("disabled")) {
      this.internals.ariaDisabled = this.disabled ? "true" : "false";
    }

    if (changedProperties.has("required")) {
      this.checkValidity();
    }
  }

  willUpdate(changedProperties: Map<string, any>) {
    super.willUpdate(changedProperties);

    if (changedProperties.has("selected") || changedProperties.has("slotContents")) {
      this._selectedOption = this.slotContents
        .filter((element) => this.selected.includes(element.value))
        .map((element) => {
          const clone = element.cloneNode(true) as Option;
          clone.selected = false;
          clone.href = undefined;
          clone.tabIndex = -1;
          return clone;
        });

      this._selectedOptions = this.slotContents
        .filter((element) => this.selected.includes(element.value))
        .map((element) => {
          const clone = element.cloneNode(true) as Option;
          clone.variant = "badge";
          clone.tabIndex = 0;
          clone.selected = true;
          return clone;
        });
    }
  }

  get filteredOptions() {
    if (!this.keyword) {
      return this.slotContents;
    }

    const results = this.slotContents
      .filter((element) => {
        const textContent = element.textContent?.toLowerCase() || "";
        return textContent.includes(this.keyword.toLowerCase());
      })
      .map((element, index) => {
        element.tabIndex = index === 0 ? 0 : -1;
        return element;
      });

    if (results.length === 0) {
      return this.slotContents;
    }

    return results;
  }

  get form() {
    return this.internals.form;
  }

  get selectedOption() {
    return this._selectedOption;
  }

  get selectedOptions() {
    return this._selectedOptions;
  }

  get value() {
    if (this.multiple) {
      return this.selected;
    } else {
      return this.selected[0] || null;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-select-advanced": SelectAdvanced;
  }
}

export default SelectAdvanced;
